import React, { useMemo, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import {
  HStack,
  useToken,
  Icon,
  Box,
  Text,
} from '@gluestack-ui/themed';
import {
  MapPin, Check, Circle, Lock,
} from 'lucide-react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
  useDerivedValue,
  useAnimatedReaction,
  runOnJS,
  SharedValue,
} from 'react-native-reanimated';
import { TimelineItem as TimelineItemType } from '../types';
import { getColorForModule } from '../../../common/utils/getColorForModule';

const AnimatedBox = Animated.createAnimatedComponent(Box);
const AnimatedText = Animated.createAnimatedComponent(Text);

interface TimelineItemProps {
  /** The timeline item data (dashboard or module) */
  item: TimelineItemType;
  /** Index of the item in the timeline */
  index: number;
  /** Shared value of the vertical scroll position of the main list */
  scrollY: SharedValue<number>;
  /** Callback when the timeline item is pressed */
  onPress: () => void;
  /** Shared value of the page height */
  pageHeightSV: SharedValue<number>;
}

/**
 * TimelineItem displays an individual entry in the sticky horizontal timeline.
 * It animates its active state based on the main scroll position.
 */
export const TimelineItem: React.FC<TimelineItemProps> = ({
  item, index, scrollY, onPress, pageHeightSV,
}) => {
  const colorName = item.type === 'dashboard' ? null : getColorForModule(item as unknown as Parameters<typeof getColorForModule>[0]);
  // @ts-expect-error - brandBase is a semantic token
  const brandBase = (useToken('colors', 'brandBase') as string) || '#000000';
  // @ts-expect-error - dynamic module token
  const moduleColor = (useToken('colors', (colorName ? `${colorName}500` : 'brandBase')) as string) || '#000000';
  const activeColor = item.type === 'dashboard' ? brandBase : moduleColor;
  // @ts-expect-error - textHeading is a semantic token
  const textHeading = (useToken('colors', 'textHeading') as string) || '#000000';
  // @ts-expect-error - textHint is a semantic token
  const textHint = (useToken('colors', 'textHint') as string) || '#000000';
  // @ts-expect-error - textSubtle is a semantic token
  const textSubtle = (useToken('colors', 'textSubtle') as string) || '#000000';
  // @ts-expect-error - borderLight is a semantic token
  const borderLight = (useToken('colors', 'borderLight') as string) || '#000000';

  const isActive = useDerivedValue(() => {
    const height = pageHeightSV.value;
    const center = index * height;
    return Math.abs(scrollY.value - center) < height / 2;
  });

  const [active, setActive] = useState(false);
  useAnimatedReaction(
    () => isActive.value,
    (res) => {
      if (res !== active) {
        runOnJS(setActive)(res);
      }
    },
    [active],
  );

  const animatedLabelStyle = useAnimatedStyle(() => ({
    opacity: withTiming(active ? 1 : 0.6),
    transform: [{ scale: withTiming(active ? 1.02 : 1) }],
  }));

  const animatedDotStyle = useAnimatedStyle(() => ({
    backgroundColor: withTiming(active ? activeColor : borderLight),
    width: withTiming(active ? 24 : 12),
    opacity: withTiming(active ? 1 : 0.4),
  }));

  const state = item.uiState || 'not-started';
  const isLocked = state === 'locked';
  const stateIconAs = item.type === 'dashboard'
    ? MapPin
    : state === 'graded' || state === 'submitted'
      ? Check
      : state === 'active'
        ? Circle
        : Lock;

  const wrappedTitle = useMemo(() => {
    if (item.type === 'dashboard') return 'JOURNEY';
    if (!item.title) return '';
    if (item.title.length <= 25) return item.title;
    return `${item.title.substring(0, 22)}...`;
  }, [item.title, item.type]);

  const animatedTextStyle = useAnimatedStyle(() => {
    const baseColor = active ? textHeading : isLocked ? textSubtle : textHint;
    return {
      color: withTiming(baseColor),
    };
  });

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      hitSlop={{
        top: 20, bottom: 20, left: 10, right: 10,
      }}
    >
      <AnimatedBox
        marginHorizontal={24}
        alignItems="center"
        paddingVertical={12}
        style={animatedLabelStyle}
      >
        <HStack space="xs" alignItems="center">
          {(state !== 'not-started' || item.type === 'dashboard') && (
            <Icon
              as={stateIconAs}
              size="xs"
              color={active ? activeColor : (isLocked ? textSubtle : textHint)}
              opacity={active ? 1 : 0.6}
            />
          )}
          <AnimatedText
            sx={{
              _text: {
                fontSize: 12,
                fontWeight: '$semibold',
                textTransform: 'uppercase',
                letterSpacing: 2,
              },
            }}
            style={animatedTextStyle}
            numberOfLines={1}
          >
            {wrappedTitle}
          </AnimatedText>
        </HStack>

        {/* Continuous Path Line */}
        <Box
          position="absolute"
          bottom={14}
          left={-24}
          right={-24}
          height={1}
          bg="$borderLight"
          opacity={0.15}
          zIndex={-1}
        />

        <AnimatedBox
          h={2}
          borderRadius={1}
          mt={10}
          alignSelf="center"
          style={animatedDotStyle}
        />
      </AnimatedBox>
    </TouchableOpacity>
  );
};
